// Surveys 2.0 answer persistence (spec §3 + §6).
//
// - The client owns the response id (`rsp_` + 20 base62) and always PUTs the
//   FULL cumulative state, so the queue coalesces: only the newest payload
//   matters, older unsent ones are dropped. One request in flight at a time.
// - Network/5xx/429 failures retry with exponential backoff (1s → 30s, with
//   jitter so visitors who failed together don't come back together; a 429's
//   Retry-After is honoured) and immediately when the browser comes back
//   online. Other 4xx are final.
// - flushWithKeepalive() is for close / pagehide: it hands the newest unsaved
//   payload to fetch(..., { keepalive: true }) so it survives the frame being
//   torn down.
// - The in-progress state is mirrored to localStorage keyed by survey id +
//   session so a reminder (resume) continues at lastBlockId.
import { useSessionStore } from "../Stores/Session";
import type { SurveyAnswers, SurveyChannel, SurveyResumeData } from "./surveyTypes";

const BASE62 =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

export const generateResponseId = (): string => {
  const bytes = new Uint8Array(20);
  try {
    (window.crypto || (window as any).msCrypto).getRandomValues(bytes);
  } catch (exp) {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let id = "rsp_";
  for (let i = 0; i < bytes.length; i++) {
    // 248 = 4 * 62 — reject the tail to keep the distribution uniform.
    let byte = bytes[i];
    while (byte >= 248) {
      byte = Math.floor(Math.random() * 248);
    }
    id += BASE62[byte % 62];
  }
  return id;
};

export interface SurveyResponsePayload {
  version: number;
  answers: SurveyAnswers;
  path: string[];
  lastBlockId?: string;
  status?: "completed";
  endingId?: string;
  channel: SurveyChannel;
  source?: Record<string, any>;
  fields?: Record<string, string>;
  pageUrl?: string;
  language?: string;
  device?: { class: string; os: string };
  personalToken?: string;
  spamToken?: string;
  /** Contact questions skipped as known: the server answers them from the session. */
  skippedKnown?: string[];
}

export type SurveySaveState = "idle" | "saving" | "saved" | "retrying" | "failed";

const authHeaders = (): Record<string, string> => {
  const { sdkKey, session } = useSessionStore.getState();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (sdkKey) headers["Api-Token"] = sdkKey;
  if (session) {
    headers["Gleap-Id"] = session.gleapId ?? "";
    headers["Gleap-Hash"] = session.gleapHash ?? "";
  }
  return headers;
};

const apiBase = () => useSessionStore.getState().apiUrl;

const surveyPath = (surveyId: string) =>
  `${apiBase()}/surveys/${encodeURIComponent(surveyId)}`;

export const fetchSurveyDefinition = async (
  surveyId: string,
  lang: string,
): Promise<{ status: number; data?: any }> => {
  const response = await fetch(
    `${surveyPath(surveyId)}?lang=${encodeURIComponent(lang)}`,
    { method: "GET", headers: authHeaders(), credentials: "omit" },
  );
  if (response.status !== 200) {
    return { status: response.status };
  }
  return { status: 200, data: await response.json() };
};

/**
 * The response this session left halfway (server copy of the local resume
 * state — the iOS SDK opens every survey in a fresh web view without
 * storage). Never waits longer than `timeoutMs`; any failure, a 204 or a
 * timeout is null (start fresh).
 */
export const fetchCurrentSurveyResponse = async (
  surveyId: string,
  timeoutMs = 1500,
): Promise<SurveyResumeData | null> => {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  let timer: any = null;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      try {
        controller?.abort();
      } catch (exp) {}
      resolve(null);
    }, timeoutMs);
  });
  const request = (async (): Promise<SurveyResumeData | null> => {
    try {
      const response = await fetch(`${surveyPath(surveyId)}/responses/current`, {
        method: "GET",
        headers: authHeaders(),
        credentials: "omit",
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (response.status !== 200) {
        response.text().catch(() => {});
        return null;
      }
      const data = await response.json();
      if (!data || typeof data.responseId !== "string" || !data.responseId) {
        return null;
      }
      return {
        responseId: data.responseId,
        version: typeof data.version === "number" ? data.version : undefined,
        answers: data.answers && typeof data.answers === "object" ? data.answers : {},
        path: Array.isArray(data.path) ? data.path : [],
        lastBlockId: typeof data.lastBlockId === "string" ? data.lastBlockId : undefined,
      };
    } catch (exp) {
      return null;
    }
  })();
  try {
    return await Promise.race([request, timeout]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * One impression per show. A plain fetch, not keepalive / sendBeacon: it is
 * sent while the survey is on screen (nothing to survive), and the endpoint
 * needs the Api-Token header, which sendBeacon cannot send. The (empty 204)
 * body is drained: Chrome cancels a response nobody reads once it is
 * collected and reports the request as net::ERR_ABORTED although it succeeded.
 */
export const postSurveyImpression = (
  surveyId: string,
  version: number,
  channel: SurveyChannel,
) => {
  try {
    fetch(`${surveyPath(surveyId)}/impressions`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ version, channel }),
      credentials: "omit",
    })
      .then((response) => response.text())
      .catch(() => {});
  } catch (exp) {}
};

const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 30000;

/** Retry-After (seconds) of a 429/503, capped at 5 minutes; 0 when absent. */
const retryAfterOf = (response: Response): number => {
  try {
    const seconds = Number(response.headers.get("Retry-After"));
    if (!isFinite(seconds) || seconds <= 0) return 0;
    return Math.min(seconds, 300) * 1000;
  } catch (exp) {
    return 0;
  }
};

export class SurveyResponseSync {
  private pending: SurveyResponsePayload | null = null;
  private inFlight: SurveyResponsePayload | null = null;
  private attempts = 0;
  private retryTimer: any = null;
  private firstWriteAcked = false;
  private stopped = false;
  private spamTokenProvider?: () => Promise<string | undefined>;

  constructor(
    private surveyId: string,
    private responseId: string,
    private onState: (state: SurveySaveState) => void,
    options?: {
      spamTokenProvider?: () => Promise<string | undefined>;
      /** Resumed responses were already created server-side. */
      alreadyCreated?: boolean;
    },
  ) {
    this.spamTokenProvider = options?.spamTokenProvider;
    this.firstWriteAcked = !!options?.alreadyCreated;
    window.addEventListener("online", this.handleOnline);
  }

  private get url() {
    return `${surveyPath(this.surveyId)}/responses/${encodeURIComponent(
      this.responseId,
    )}`;
  }

  private handleOnline = () => {
    if (this.retryTimer && (this.pending || this.inFlight)) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.attempts = 0;
      this.pump();
    }
  };

  /** Queue the newest full state; older unsent states are superseded. */
  save(payload: SurveyResponsePayload) {
    if (this.stopped) return;
    this.pending = payload;
    this.onState(this.retryTimer ? "retrying" : "saving");
    this.pump();
  }

  get hasUnsaved() {
    return !!(this.pending || this.inFlight);
  }

  private async pump() {
    if (this.inFlight || this.retryTimer || !this.pending || this.stopped) {
      return;
    }
    const payload = this.pending;
    this.pending = null;
    this.inFlight = payload;

    let body: SurveyResponsePayload = payload;
    if (!this.firstWriteAcked && this.spamTokenProvider) {
      try {
        const token = await this.spamTokenProvider();
        if (token) body = { ...payload, spamToken: token };
      } catch (exp) {}
    }

    let retry = false;
    let retryAfterMs = 0;
    try {
      const response = await fetch(this.url, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(body),
        credentials: "omit",
      });
      // Drain the body (see postSurveyImpression: unread responses show up
      // as net::ERR_ABORTED in the network panel).
      response.text().catch(() => {});
      if (response.ok) {
        this.firstWriteAcked = true;
        this.attempts = 0;
      } else if (
        response.status >= 500 ||
        response.status === 429 ||
        response.status === 408
      ) {
        retry = true;
        retryAfterMs = retryAfterOf(response);
      } else {
        // 4xx: the server refused this response (closed survey, one per
        // person, …). Retrying would never succeed — stop quietly.
        this.inFlight = null;
        this.pending = null;
        this.onState("failed");
        return;
      }
    } catch (exp) {
      retry = true;
    }

    if (retry) {
      // Keep the newest state: a newer pending payload wins over the failed one.
      if (!this.pending) this.pending = payload;
      this.inFlight = null;
      this.attempts++;
      const backoff = Math.min(
        RETRY_MAX_MS,
        RETRY_MIN_MS * Math.pow(2, this.attempts - 1),
      );
      // 50–100 % of the backoff, never sooner than the server asked for.
      const delay = Math.max(
        retryAfterMs,
        Math.round(backoff * (0.5 + Math.random() * 0.5)),
      );
      this.onState("retrying");
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.pump();
      }, delay);
      return;
    }

    this.inFlight = null;
    if (this.pending) {
      this.pump();
    } else {
      this.onState("saved");
    }
  }

  /**
   * Close / pagehide: send the newest unsaved state with keepalive so it
   * outlives the frame. Safe to call repeatedly; no-op when all is saved.
   */
  flushWithKeepalive() {
    const payload = this.pending ?? this.inFlight;
    if (!payload) return;
    try {
      fetch(this.url, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: "omit",
      }).catch(() => {});
      this.pending = null;
    } catch (exp) {}
  }

  stop() {
    this.stopped = true;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    window.removeEventListener("online", this.handleOnline);
  }
}

// ── Local resume state ─────────────────────────────────────────────────────

export interface StoredSurveyState {
  responseId: string;
  /** Published definition version this state was rendered with. */
  version: number;
  /** Version the response was started on (differs after a resume across a publish). */
  responseVersion?: number;
  answers: SurveyAnswers;
  path: string[];
  lastBlockId?: string;
  completed?: boolean;
  /** At least one answer reached the queue (server doc may exist). */
  started?: boolean;
  updatedAt: number;
}

const STORAGE_PREFIX = "gleap-survey-v2";
// Matches the server: a response turns `partial` 30 minutes after its last
// answer. A reminder (resume) may continue it for much longer.
const FRESH_MS = 30 * 60 * 1000;
const RESUME_MAX_MS = 30 * 24 * 60 * 60 * 1000;

const storageKey = (surveyId: string) => {
  const session = useSessionStore.getState().session;
  return `${STORAGE_PREFIX}-${surveyId}-${session?.gleapId || "anon"}`;
};

export const readSurveyState = (
  surveyId: string,
  version: number,
  resume: boolean,
): StoredSurveyState | null => {
  try {
    const raw = localStorage.getItem(storageKey(surveyId));
    if (!raw) return null;
    const state = JSON.parse(raw) as StoredSurveyState;
    if (!state || state.completed || state.version !== version) return null;
    if (!state.responseId || !Array.isArray(state.path)) return null;
    const age = Date.now() - (state.updatedAt || 0);
    if (age > (resume ? RESUME_MAX_MS : FRESH_MS)) return null;
    return state;
  } catch (exp) {
    return null;
  }
};

/** Can this frame keep resume state? False when storage is blocked or the web view has none. */
export const surveyStorageAvailable = (): boolean => {
  try {
    const probe = `${STORAGE_PREFIX}-probe`;
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch (exp) {
    return false;
  }
};

/**
 * Is there local resume state for this survey at all (any version)? When
 * there is, the server copy is not asked for.
 */
export const hasStoredSurveyState = (surveyId: string, resume: boolean): boolean => {
  try {
    const raw = localStorage.getItem(storageKey(surveyId));
    if (!raw) return false;
    const state = JSON.parse(raw) as StoredSurveyState;
    if (!state || state.completed || !state.responseId) return false;
    return Date.now() - (state.updatedAt || 0) <= (resume ? RESUME_MAX_MS : FRESH_MS);
  } catch (exp) {
    return false;
  }
};

export const writeSurveyState = (surveyId: string, state: StoredSurveyState) => {
  try {
    localStorage.setItem(storageKey(surveyId), JSON.stringify(state));
  } catch (exp) {}
};

export const clearSurveyState = (surveyId: string) => {
  try {
    localStorage.removeItem(storageKey(surveyId));
  } catch (exp) {}
};
